/**
 * The dependency-free half of `shared`.
 *
 * `shared.ts` builds ~376 zod schemas at module scope, and a top-level `z.object(...)` is a CALL no bundler can
 * prove side-effect-free — so importing a single pure helper from the barrel shipped zod's core to the browser:
 * 76 KB minified on EVERY authenticated page, via `route → use-permissions → shared → zod`. Anything the CLIENT
 * needs at runtime and that does not itself need a schema belongs here; `shared.ts` re-exports all of it.
 */

import type { SortDirection } from './shared.ts'

/** Split `batch` into slices of at most `itemPerBatch`. */
export function splitInBatches<T>(batch: T[], itemPerBatch: number): T[][] {
  const slices = []
  for (let i = 0; i < batch.length; i += itemPerBatch) {
    slices.push(batch.slice(i, Math.min(i + itemPerBatch, batch.length)))
  }

  return slices
}

/** Order numbers with nulls always last, whichever direction is being sorted. */
export function compareNullsLast(
  a: number | null | undefined,
  b: number | null | undefined,
  direction: SortDirection | undefined,
): number {
  if (a == null && b == null) return 0
  if (a == null) return 1
  if (b == null) return -1
  return direction === 'asc' ? a - b : b - a
}

/**
 * The role an actor acts WITH: a company account is always its own administrator, an employee acts with the
 * `employeeType` of the company it is currently viewing, and a personal account has no company role.
 */
export function effectiveCompanyRole(user: {
  type: 'company' | 'applicant'
  activeCompanyId?: number | null
  activeEmployer?: { employeeType?: import('./shared.ts').EmployeeRole | null } | null
}): import('./shared.ts').EmployeeRole | null {
  if (user.type === 'company') return 'administrator'
  if (user.activeCompanyId != null) return user.activeEmployer?.employeeType ?? null
  return null
}

/**
 * The company whose DIRECTORY this actor may browse. Unlike `interfaceMode` (which view is on screen) this is a
 * question about the ROLE alone: `administrador` and `autorizado` both reach the whole directory from BOTH views,
 * and differ only in WRITES, which stay gated by `CompanyPermission`.
 *
 * EQUIPOS ARE THE ONE EXCEPTION: a team the actor was explicitly denied stays out of every list and report in
 * either view — that per-team restriction prevails over the role. See `getUnauthorizedTeamIds` in teams.service.
 */
export function companyDirectoryOwnerId(user: {
  id: number
  type: 'company' | 'applicant'
  activeCompanyId?: number | null
  activeEmployer?: { employeeType?: import('./shared.ts').EmployeeRole | null } | null
}): number | null {
  const role = effectiveCompanyRole(user)
  if (role !== 'administrator' && role !== 'authorized') return null
  return user.type === 'company' ? user.id : (user.activeCompanyId ?? null)
}
